import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { ApiError } from '@/lib/api/client';
import { errorMessage } from '@/lib/api/errorMessages';

/**
 * Loading, error and empty states.
 *
 * Every data view handles all three explicitly (`frontend-standards.md` section 11), and
 * they live together because they are one decision, not three.
 */

export function FullPageLoader({ label = 'در حال بارگذاری…' }: { label?: string }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 8 }} aria-live="polite">
      <CircularProgress size={28} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

/**
 * An error, with a retry.
 *
 * @remarks
 * The server's `detail` text is deliberately **not** rendered. It is not guaranteed free of
 * GitLab vocabulary, and leaking that to a non-technical user is a defect rather than a
 * rough edge (`frontend-standards.md` section 6). A localized message is chosen from the
 * structured `errorCode` instead, falling back to something generic rather than to server
 * prose.
 *
 * The correlation id *is* shown, because it is the one thing that makes a support
 * conversation short, and it carries no meaning to anyone without log access.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const apiError = error instanceof ApiError ? error : null;

  return (
    <Paper sx={{ p: 3 }} role="alert">
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="h4">{titleFor(apiError)}</Typography>

        <Typography variant="body2" color="text.secondary">
          {errorMessage(error, 'انجام این کار ممکن نشد. اگر ادامه داشت، شناسه پیگیری را به مدیر سامانه بدهید.')}
        </Typography>

        {apiError?.correlationId ? (
          <Typography variant="caption" color="text.disabled" className="mono">
            شناسه پیگیری: {apiError.correlationId}
          </Typography>
        ) : null}

        {onRetry ? (
          <Button variant="outlined" size="small" onClick={onRetry}>
            تلاش دوباره
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
}

function titleFor(error: ApiError | null): string {
  if (error?.isNotFound) {
    return 'یافت نشد';
  }

  if (error?.isForbidden) {
    return 'دسترسی ندارید';
  }

  return 'مشکلی پیش آمد';
}

/**
 * An empty state.
 *
 * @remarks
 * These carry unusual weight in this product. The organization is starting from zero data,
 * so an empty list is the *normal* first experience rather than an edge case
 * (`frontend-standards.md` section 11). Each one names the next action instead of reading
 * like something broke.
 */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <Paper sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
      <Stack spacing={1.5} alignItems="center">
        <Box
          aria-hidden
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            border: 1,
            borderColor: 'divider',
            color: 'primary.main',
            fontSize: 20,
          }}
        >
          ✦
        </Box>

        <Typography variant="h4">{title}</Typography>

        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
          {description}
        </Typography>

        {action}
      </Stack>
    </Paper>
  );
}
